import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Container, 
  Grid, 
  Paper, 
  Button, 
  Chip,
  Divider,
  Stack
} from '@mui/material';
import CodeIcon from '@mui/icons-material/Code';
import ApiIcon from '@mui/icons-material/Api';
import LockIcon from '@mui/icons-material/Lock';
import SpeedIcon from '@mui/icons-material/Speed';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TerminalIcon from '@mui/icons-material/Terminal';
import KeyIcon from '@mui/icons-material/Key';
import SyncIcon from '@mui/icons-material/Sync';

function Developers() {
  const endpoints = [
    { method: 'GET', path: '/v1/assets/price', desc: 'Retrieve real-time pricing for all assets.' },
    { method: 'POST', path: '/v1/assets/purchase', desc: 'Securely initiate an asset purchase using your API key.' },
    { method: 'GET', path: '/v1/assets/{id}/certificate', desc: 'Fetch blockchain-verified certificates for your assets.' },
  ];

  return (
    <Box sx={{ backgroundColor: '#050505', minHeight: '100vh', overflowX: 'hidden' }}>
      
      {/* ============ HERO SECTION ============ */}
      <Box
        sx={{
          position: 'relative',
          height: '70vh',
          minHeight: '600px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          overflow: 'hidden',
          backgroundImage: 'url(/images/hero.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <Box sx={{ 
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, 
          background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0.75) 50%, rgba(0, 0, 0, 0.95) 100%)', 
          zIndex: 1 
        }} />
        
        <Box sx={{ 
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, 
          backgroundImage: 'url(/images/hero.png)', backgroundSize: 'cover', backgroundPosition: 'center', 
          animation: 'slowZoom 25s ease-in-out infinite alternate', 
          '@keyframes slowZoom': { '0%': { transform: 'scale(1)' }, '100%': { transform: 'scale(1.1)' } }, 
          zIndex: 0 
        }} />

        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 10, pt: { xs: '140px', md: '180px' } }}>
          
          <Typography variant="h1" component="h1" sx={{
            fontFamily: "'Playfair Display', serif", fontWeight: 600, fontSize: { xs: '3rem', sm: '3.5rem', md: '5rem' }, lineHeight: 1.1, mb: 4,
            background: 'linear-gradient(90deg, #ffffff 0%, #d4af37 50%, #ffffff 100%)', backgroundSize: '200% auto',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', animation: 'shimmer 6s linear infinite',
            filter: 'drop-shadow(0 4px 15px rgba(0,0,0,0.8))',
            '@keyframes shimmer': { '0%': { backgroundPosition: '200% center' }, '100%': { backgroundPosition: '-200% center' } },
          }}>
            Build With <br /> Diamond Dollar
          </Typography>

          <Typography variant="body1" sx={{ color: '#e0e0e0', fontSize: { xs: '1rem', md: '1.2rem' }, fontWeight: 300, maxWidth: '700px', mx: 'auto', mb: 5, lineHeight: 1.8, textShadow: '0 2px 10px rgba(0,0,0,0.8)' }}>
            Access our secure, institutional-grade API to integrate real-time asset pricing, 
            blockchain verification, and purchase functionality directly into your own applications, 
            trading platforms, or wealth management systems.
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button component={RouterLink} to="/contact" variant="contained" endIcon={<ArrowForwardIcon />} sx={{
              background: 'linear-gradient(135deg, #d4af37 0%, #b8962f 100%)', 
              color: '#000000', 
              fontWeight: 800, 
              fontSize: '0.85rem', 
              letterSpacing: '2px', 
              textTransform: 'uppercase', 
              padding: '18px 40px', 
              borderRadius: '4px', 
              boxShadow: '0 10px 40px rgba(212, 175, 55, 0.4)', 
              transition: 'all 0.5s ease',
              '&:hover': { 
                background: 'linear-gradient(135deg, #e8c84a 0%, #d4af37 100%)', 
                color: '#000000', 
                boxShadow: '0 15px 50px rgba(212, 175, 55, 0.5)', 
                transform: 'translateY(-3px)' 
              },
            }}>
              View Documentation
            </Button>
            <Button component={RouterLink} to="/contact" variant="outlined" sx={{
              borderColor: '#d4af37', 
              color: '#d4af37', 
              fontWeight: 700, 
              fontSize: '0.85rem', 
              letterSpacing: '2px', 
              textTransform: 'uppercase', 
              padding: '18px 40px', 
              borderRadius: '4px',
              '&:hover': { borderColor: '#ffffff', color: '#ffffff', backgroundColor: 'rgba(212, 175, 55, 0.1)' },
            }}>
              Request API Key
            </Button>
          </Stack>
        </Container>
      </Box>
      {/* ============ END HERO ============ */}

      {/* ============ MAIN CONTENT ============ */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        
        {/* Code Terminal Mockup */}
        <Grid container spacing={6} alignItems="center" sx={{ mb: 12 }}>
          <Grid item xs={12} md={5}>
            <Paper
              elevation={0}
              sx={{
                background: 'linear-gradient(135deg, #111111 0%, #0a0a0a 100%)',
                border: '1px solid rgba(212, 175, 55, 0.3)',
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: '0 20px 50px rgba(0, 0, 0, 0.8), 0 0 20px rgba(212, 175, 55, 0.1)',
                fontFamily: 'monospace',
              }}
            >
              <Box sx={{ backgroundColor: '#1a1a1a', px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid rgba(212, 175, 55, 0.1)' }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ff5f56' }} />
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ffbd2e' }} />
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#27c93f' }} />
                <Typography variant="caption" sx={{ ml: 2, color: '#a1a1a1', fontWeight: 600 }}>
                  diamond_dollar.js
                </Typography>
              </Box>

              <Box sx={{ p: 4 }}>
                <Typography variant="body2" sx={{ color: '#c0c0c0', lineHeight: 2 }}>
                  <span style={{ color: '#a1a1a1' }}>1.</span> <span style={{ color: '#ffffff' }}>const</span> <span style={{ color: '#61afef' }}>diamondDollar</span> = <span style={{ color: '#d4af37' }}>require</span>(<span style={{ color: '#98c379' }}>'@dd/sdk'</span>);<br/>
                  <span style={{ color: '#a1a1a1' }}>2.</span><br/>
                  <span style={{ color: '#a1a1a1' }}>3.</span> <span style={{ color: '#ffffff' }}>await</span> diamondDollar.<span style={{ color: '#61afef' }}>authenticate</span>({'{'}<br/>
                  <span style={{ color: '#a1a1a1' }}>4.</span>   apiKey: process.env.<span style={{ color: '#98c379' }}>DD_API_KEY</span><br/>
                  <span style={{ color: '#a1a1a1' }}>5.</span> {'}'});<br/>
                  <span style={{ color: '#a1a1a1' }}>6.</span><br/>
                  <span style={{ color: '#a1a1a1' }}>7.</span> <span style={{ color: '#ffffff' }}>const</span> price = <span style={{ color: '#ffffff' }}>await</span> diamondDollar.<span style={{ color: '#61afef' }}>getPrice</span>(<span style={{ color: '#98c379' }}>'DIA-2026'</span>);
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={7}>
            <Typography variant="h2" sx={{ fontFamily: "'Playfair Display', serif", color: '#ffffff', mb: 3, fontSize: { xs: '2rem', md: '3rem' } }}>
              Seamless Integration
            </Typography>
            <Typography variant="body1" sx={{ color: '#a1a1a1', lineHeight: 1.8, mb: 6 }}>
              Our APIs are designed to be simple, secure, and highly available. Whether you're building a simple pricing widget or a full trading platform, our infrastructure scales with you.
            </Typography>
            
            {/* Quick Steps */}
            <Stack spacing={3} sx={{ mb: 6 }}>
              {[
                { icon: <KeyIcon sx={{ color: '#d4af37' }} />, title: '1. Get Your Key', desc: 'Sign up for an API key to get started.' },
                { icon: <SyncIcon sx={{ color: '#d4af37' }} />, title: '2. Integrate', desc: 'Install the SDK or call our REST endpoints.' },
                { icon: <TerminalIcon sx={{ color: '#d4af37' }} />, title: '3. Go Live', desc: 'Launch and scale with 99.99% uptime.' },
              ].map((step, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <Box sx={{ width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px', border: '1px solid rgba(212, 175, 55, 0.3)', backgroundColor: 'rgba(212, 175, 55, 0.05)' }}>
                    {step.icon}
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontFamily: "'Playfair Display', serif", color: '#ffffff', fontSize: '1rem', mb: 0.5 }}>{step.title}</Typography>
                    <Typography variant="body2" sx={{ color: '#a1a1a1', fontSize: '0.85rem' }}>{step.desc}</Typography>
                  </Box>
                </Box>
              ))}
            </Stack>

            <Box sx={{ display: 'flex', gap: 4 }}>
              <Box>
                <Typography variant="h3" sx={{ fontFamily: "'Playfair Display', serif", color: '#d4af37', fontSize: '2.5rem' }}>99.99%</Typography>
                <Typography variant="caption" sx={{ color: '#a1a1a1', textTransform: 'uppercase', letterSpacing: '1px' }}>Uptime</Typography>
              </Box>
              <Box>
                <Typography variant="h3" sx={{ fontFamily: "'Playfair Display', serif", color: '#d4af37', fontSize: '2.5rem' }}>50ms</Typography>
                <Typography variant="caption" sx={{ color: '#a1a1a1', textTransform: 'uppercase', letterSpacing: '1px' }}>Response Time</Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Features (Premium 3x1 Grid) */}
        <Grid container spacing={3} sx={{ mb: 12 }}>
          {[
            { icon: <SpeedIcon />, title: 'Ultra-Fast', desc: 'Sub-50ms response times for real-time pricing updates.' },
            { icon: <LockIcon />, title: 'Enterprise Security', desc: '256-bit encryption, IP whitelisting, and role-based access.' },
            { icon: <ApiIcon />, title: 'RESTful & WebSockets', desc: 'Seamlessly integrate using our documented REST API or real-time streams.' },
          ].map((item, index) => (
            <Grid item xs={12} md={4} key={index} sx={{ height: '100%' }}>
              <Paper
                elevation={0}
                sx={{
                  p: 3.5,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  background: 'linear-gradient(135deg, #141414 0%, #0a0a0a 100%)',
                  border: '1px solid rgba(212, 175, 55, 0.15)',
                  borderRadius: '6px',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.4s ease',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '50%',
                    background: 'linear-gradient(to bottom, rgba(255,255,255,0.05), transparent)',
                    pointerEvents: 'none',
                  },
                  '&:hover': { 
                    borderColor: '#d4af37', 
                    transform: 'translateY(-5px)', 
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.8), 0 0 20px rgba(212, 175, 55, 0.2)',
                    '& .dev-icon-box': {
                      borderColor: '#d4af37',
                      color: '#d4af37',
                      backgroundColor: 'rgba(212, 175, 55, 0.1)',
                    }
                  },
                }}
              >
                <Box 
                  className="dev-icon-box"
                  sx={{ 
                    color: '#d4af37', 
                    height: '60px', 
                    width: '60px',
                    mb: 3, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    borderRadius: '4px',
                    border: '1px solid rgba(212, 175, 55, 0.3)',
                    backgroundColor: 'rgba(212, 175, 55, 0.05)',
                    transition: 'all 0.4s ease',
                    '& svg': { fontSize: '2.5rem' } 
                  }}
                >
                  {item.icon}
                </Box>
                <Typography variant="h5" sx={{ fontFamily: "'Playfair Display', serif", color: '#fff', mb: 1, fontSize: '1.2rem' }}>
                  {item.title}
                </Typography>
                <Typography variant="body2" sx={{ color: '#a1a1a1', lineHeight: 1.8, fontSize: '0.9rem' }}>
                  {item.desc}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* API Endpoints List */}
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mb: 5, flexWrap: 'wrap' }}>
            <Typography variant="h3" sx={{ fontFamily: "'Playfair Display', serif", color: '#fff' }}>
              Core Endpoints
            </Typography>
          </Box>
          <Paper
            elevation={0}
            sx={{
              background: 'linear-gradient(135deg, #111111 0%, #0a0a0a 100%)',
              border: '1px solid rgba(212, 175, 55, 0.3)',
              borderRadius: '6px',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.8)',
            }}
          >
            {endpoints.map((ep, index) => (
              <Box key={index}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  padding: '25px 30px', 
                  gap: 3, 
                  flexWrap: 'wrap',
                  transition: 'background-color 0.3s ease',
                  '&:hover': { backgroundColor: 'rgba(212, 175, 55, 0.03)' }
                }}>
                  <Chip
                    label={ep.method}
                    size="small"
                    sx={{
                      bgcolor: ep.method === 'GET' ? 'rgba(212, 175, 55, 0.15)' : 'rgba(212, 175, 55, 0.1)',
                      color: '#d4af37',
                      fontWeight: 700,
                      borderRadius: '4px',
                    }}
                  />
                  <Typography variant="h6" sx={{ color: '#d4af37', fontFamily: 'monospace', fontWeight: 400, flex: 1, minWidth: '200px', fontSize: '1rem' }}>
                    {ep.path}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#a1a1a1', flex: 2, minWidth: '250px' }}>
                    {ep.desc}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<ContentCopyIcon sx={{ fontSize: '16px' }} />}
                    sx={{
                      color: '#d4af37',
                      '&:hover': { color: '#ffffff' },
                      textTransform: 'none',
                    }}
                  >
                    Copy
                  </Button>
                </Box>
                {index !== endpoints.length - 1 && <Divider sx={{ borderColor: 'rgba(212, 175, 55, 0.05)' }} />}
              </Box>
            ))}
          </Paper>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5, flexDirection: 'column', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ color: '#a1a1a1', display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleIcon sx={{ color: '#d4af37', fontSize: '18px' }} />
              99.99% API Uptime Guarantee. All requests are logged for auditing.
            </Typography>
          </Box>
        </Box>

      </Container>
    </Box>
  );
}

export default Developers;